import { getTopLeft } from 'ol/extent';
import { GeoJSON, GML, GPX, KML, TopoJSON } from 'ol/format'
import DragAndDrop from 'ol/interaction/DragAndDrop'
import { Vector as VectorLayer } from 'ol/layer'
import ImageLayer from 'ol/layer/Image';
import TileLayer from 'ol/layer/Tile'
import { get as getProjection, transformExtent } from 'ol/proj';
import { register } from 'ol/proj/proj4';
import { ImageWMS, TileWMS, Vector as VectorSource, WMTS } from 'ol/source'
import { Circle, Fill, Stroke, Style } from 'ol/style'
import WMTSTileGrid from 'ol/tilegrid/WMTS';

import proj4 from 'proj4';

import { SHP } from './shp'
import { loadGpsFeatures } from '../gps'
import { bluePolygonStyle } from '../styles'
import { substitute } from './substituteWms'
import { getGeonorgeToken, setGeonorgeToken } from '../tokens'
import { wmsSettings } from '../wms'

proj4.defs('EPSG:32632','+proj=utm +zone=32 +ellps=WGS84 +datum=WGS84 +units=m +no_defs');
proj4.defs('EPSG:32633','+proj=utm +zone=33 +ellps=WGS84 +datum=WGS84 +units=m +no_defs');
proj4.defs('EPSG:32635','+proj=utm +zone=35 +ellps=WGS84 +datum=WGS84 +units=m +no_defs');
// EUREF89 (Norway)
proj4.defs('EPSG:25832','+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs');
proj4.defs('EPSG:25833','+proj=utm +zone=33 +ellps=GRS80 +units=m +no_defs');
proj4.defs('EPSG:25835','+proj=utm +zone=35 +ellps=GRS80 +units=m +no_defs');
proj4.defs('EPSG:4258','+proj=longlat +ellps=GRS80 +no_defs');

register(proj4);

// function updateProjection() in functions.js
const epsg25832 = getProjection('EPSG:25832');
const epsg25833 = getProjection('EPSG:25833');
const epsg25835 = getProjection('EPSG:25835');
// extents taken from http://spatialreference.org
epsg25832.setExtent([265948.8191, 6421521.2254, 677786.3629, 7288831.7014]);
epsg25833.setExtent([227879.8880, 3932632.6543, 1044484.3835, 8893131.0281]);
epsg25835.setExtent([261450.8249, 7668433.6301, 619396.7507, 8893131.0281]);

// extents taken from epsg.io
epsg25832.setExtent([239323.445, 4294418.797, 760676.555, 9320086.207]);
epsg25833.setExtent([269387.694, 5142865.885, 731380.979, 9330126.131]);
epsg25835.setExtent([330890.119, 6615141.394, 669109.881, 9330120.056]);

// Include Finland, to show a wider OSM og Forhindre klippet TileWMSer:
const e = [-2, 56, 40, 73]; // Finland
// const hele = [4.6, 57.5, 33.2, 71.3]; // TileWMS
[32, 33, 35].forEach(utm => getProjection(`EPSG:258${utm}`).setExtent(
  transformExtent(e, 'EPSG:4258', `EPSG:258${utm}`))
);

// taken from http://www.kartverket.no/kart/gratis-kartdata/wms-tjenester/
const mapExtent = {
  32: [-2000000.0, 3500000.0, 3545984.0, 9045984.0],
  33: [-2500000.0, 3500000.0, 3045984.0, 9045984.0],
  35: [-3500000.0, 3500000.0, 2045984.0, 9045984.0]
};

const matrixIds = new Array(18).fill(0).map((a, i) => i);

// new norge i bilder
const NIBResolutions = [
  21674.7100160867,
  10837.35500804335,
  5418.677504021675,
  2709.3387520108377,
  1354.6693760054188,
  677.3346880027094,
  338.6673440013547,
  169.33367200067735,
  84.66683600033868,
  42.33341800016934,
  21.16670900008467,
  10.583354500042335,
  5.291677250021167,
  2.6458386250105836,
  1.3229193125052918,
  0.6614596562526459,
  0.33072982812632296,
  0.16536491406316148
];

// Background basemaps
const kv = 'https://cache.kartverket.no/v1/service';
const tc = `${import.meta.env.VITE_NIB_WMTS}`;
const gridResolutions = [
  21664, 10832, 5416, 2708, 1354, 677, 338.5, 169.25, 84.625, 42.3125, 21.15625,
  10.578125, 5.2890625, 2.64453125, 1.322265625, 0.6611328125, 0.33056640625, 0.165283203125
];

const getWmtsUrl = (nib, zone) => {
  const token = getGeonorgeToken() ?? '';
  return nib ? `${tc}/utm${zone}_euref89?token=${token}` : kv;
};

// When token is received, update url of all WMTSes:
export const updateWMTSes = (token) => {
  setGeonorgeToken(token); // will be used for minimaps too;
  for (const [type, projections] of Object.entries(basemapSource)) {
    if (type === 'ingen') return;
    for (const [projection, source] of Object.entries(projections)) {
      // source.setUrl(source.getUrl() + token); // did not work as of ol@6.9.0
      const zone = projection.slice(-2);
      const nib = (type === 'flybilder');
      source.setUrl(getWmtsUrl(nib, zone));
    }
  }
};

export const drawnSource = new VectorSource({ wrapX: false });
export const drawnVectorLayer = new VectorLayer({
  id: 'drawnVectorLayer',
  source: drawnSource,
  style: new Style({
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.5)'
    }),
    stroke: new Stroke({
      color: '#2196F3',
      width: 3
    }),
    image: new Circle({
      radius: 7,
      fill: new Fill({
        color: '#ffcc33'
      })
    })
  }),
  zIndex: 2
});

export const removableBorders = new VectorLayer({
  id: 'removableBorders',
  source: new VectorSource({}),
  style: new Style({
    fill: new Fill({ color: 'transparent' }),
    stroke: new Stroke({ color: 'red', lineDash: [4, 10], width: 5 }),
    image: new Circle({ radius: 7, fill: new Fill({ color: 'transparent' }) })
  }),
  zIndex: 2
});

/* Original features from backend */
export const vectorLayer = new VectorLayer({
  id: 'vectorLayer',
  source: new VectorSource({}),
  style: new Style({
    fill: new Fill({ color: 'rgba(15, 255, 255, 0)' }),
    stroke: new Stroke({
      // color: 'red',
      color: 'rgba(255, 0, 0, 0)',
      width: 2
    }),
    image: new Circle({
      radius: 7,
      fill: new Fill({ color: 'red' })
    })
  }),
  zIndex: 2
});

/* Updated features from backend */
export let updatedVectorLayer = new VectorLayer({
  id: 'updatedVectorLayer',
  source: new VectorSource({}),
  style: bluePolygonStyle,
  zIndex: 2
});

export const id2WMS = {
  RVR_F: 'vaarbeiteWMS', RSO_F: 'sommerbeiteWMS', RHS_F: 'hostbeiteWMS',
  RHV_F: 'hostvinterbeiteWMS', RVI_F: 'vinterbeiteWMS',
  ROP_F: 'oppsamlingWMS', RBH_F: 'beitehageWMS',
  RDR_F: 'flyttleiWMS', RTR_L: 'trekkleiWMS',
  RAN_L: 'linjeWMS', RAN_P: 'punktWMS',
  RAV_F: 'avtaleWMS', RDG_F: 'reinbeitedistriktWMS', REB_F: 'reinbeiteomradeWMS',
  REO_F: 'ekspropriasjonWMS', RKO_F: 'konvensjonWMS', RKS_F: 'konsesjonWMS',
  RRO_F: 'restriksjonWMS', RRO_L: 'restriksjonWMS', RSI_F: 'siidagrenseWMS'
};

export function createWMS(id) {
  const s = wmsSettings[id];
  return {
    // I am forced to multiple layers according to projection, oterwise openlayers
    // will not manage to show them correctly when maps have different projections
    image: {
      'dynamicProjection': createImageWMS(id, s.url, s.layers, 'auto', s.opacity),
      'EPSG:25832': createImageWMS(id, s.url, s.layers, '32', s.opacity),
      'EPSG:25833': createImageWMS(id, s.url, s.layers, '33', s.opacity),
      'EPSG:25835': createImageWMS(id, s.url, s.layers, '35', s.opacity)
    },
    tile: createTileWMS(id, s.url, s.layers, s.opacity)
  };
}
// ImageWMS, the only problem is that it does not work well for minimaps:
function createImageWMS(id, url, layers, utm, op, v) {
  const projection = 'EPSG:258' + utm;
  return new ImageLayer({
    id: id,
    opacity: op,
    ratio: 1,
    source: (!url) ? null : new ImageWMS({
      crossOrigin: 'anonymous',
      url: url,
      params: { LAYERS: layers, VERSION: v ?? '1.3.0' },
      serverType: 'geoserver',
      projection: (utm === 'auto') ? '' : projection
    })
  });
}
// TileWMS, the only problem is that it is clipped on current projection
export function createTileWMS(id, url, layers, op, v) {
  return new TileLayer({
    id: id,
    opacity: op,
    source: (!url) ? null : new TileWMS({
      crossOrigin: 'anonymous',
      hidpi: false,
      params: { LAYERS: layers, TILED: true, VERSION: v ?? '1.3.0' },
      ratio: 1,
      url: url
    })
  });
}

const basemapName = (type, zone) => {
  let name = 'ingen';
  switch (type) {
    case 'graatoner':
      name = 'topograatone';
      break;
    case 'flybilder':
      name = `Nibcache_UTM${zone}_EUREF89_v2`;
      break;
    case 'raster':
      name = 'toporaster';
      break;
    case 'farger':
      name = 'topo';
      break;
    default:
      name = 'ingen';
  }
  return name;
};

export const newBaseMap = (type, utm, mini) => {
  const projection = 'EPSG:258' + utm;
  // Minimaps need new/different layers to avoid flickering:
  const n = basemapName(type, utm);
  const z = utm;
  const nib = (type === 'flybilder');
  const source = mini ? createWMTS(n, z, nib) : basemapSource[type][projection];
  if (!mini && type === 'graatoner') {
    source.on('tileloadstart', () => substitute(1));
    source.on('tileloadend', () => substitute(-1));
    source.on('tileloaderror', e => substitute(-1, e.tile));
  };
  // return new TileLayer({ id: type + utm, type, source, zIndex: nib ? 1 : 0 });
  if (mini && type === 'graatoner') {
    const wms = createWMS('gråtoneWMS').image.dynamicProjection;
    wms.set('type', 'graatoner');
    return wms;
  } else
    return new TileLayer({ id: type + utm, type, source, zIndex: nib ? 1 : 0 });
};

const ingen = new WMTS({});

const createWMTS = (name, zone, nib) => {
  return new WMTS({
    layer: name,
    matrixSet: nib ? 'default028mm' : `utm${zone}n`,
    format: `image/${nib ? 'jpg' : ''}png`,
    projection: 'EPSG:258' + zone,
    tileGrid: new WMTSTileGrid({
      origin: getTopLeft(mapExtent[zone]),
      resolutions: gridResolutions,
      matrixIds: matrixIds
    }),
    style: 'default',
    // extent: mapExtent[zone],
    url: getWmtsUrl(nib, zone)
  })
};

// Different sources with different projection and origin:
const basemapSource = {
  graatoner: {
    'EPSG:25832': createWMTS('topograatone', 32, false),
    'EPSG:25833': createWMTS('topograatone', 33, false),
    'EPSG:25835': createWMTS('topograatone', 35, false)
  },
  flybilder: {
    'EPSG:25832': createWMTS('Nibcache_UTM32_EUREF89_v2', 32, true),
    'EPSG:25833': createWMTS('Nibcache_UTM33_EUREF89_v2', 33, true),
    'EPSG:25835': createWMTS('Nibcache_UTM35_EUREF89_v2', 35, true)
  },
  raster: {
    'EPSG:25832': createWMTS('toporaster', 32, false),
    'EPSG:25833': createWMTS('toporaster', 33, false),
    'EPSG:25835': createWMTS('toporaster', 35, false)
  },
  farger: {
    'EPSG:25832': createWMTS('topo', 32, false),
    'EPSG:25833': createWMTS('topo', 33, false),
    'EPSG:25835': createWMTS('topo', 35, false)
  },
  ingen: {
    'EPSG:25832': ingen,
    'EPSG:25833': ingen,
    'EPSG:25835': ingen
  }
};
// We could use the same as main map, but I do not want dynamic projection on minimaps
export const basemap = {
  graatoner: {
    'EPSG:25832': newBaseMap('graatoner', '32'),
    'EPSG:25833': newBaseMap('graatoner', '33'),
    'EPSG:25835': newBaseMap('graatoner', '35')
  },
  flybilder: {
    'EPSG:25832': newBaseMap('flybilder', '32'),
    'EPSG:25833': newBaseMap('flybilder', '33'),
    'EPSG:25835': newBaseMap('flybilder', '35')
  },
  raster: {
    'EPSG:25832': newBaseMap('raster', '32'),
    'EPSG:25833': newBaseMap('raster', '33'),
    'EPSG:25835': newBaseMap('raster', '35')
  },
  farger: {
    'EPSG:25832': newBaseMap('farger', '32'),
    'EPSG:25833': newBaseMap('farger', '33'),
    'EPSG:25835': newBaseMap('farger', '35')
  },
  ingen: {
    'EPSG:25832': newBaseMap('ingen', '32'),
    'EPSG:25833': newBaseMap('ingen', '33'),
    'EPSG:25835': newBaseMap('ingen', '35')
  }
};

// Fix shift between contour and fill when scrollbar dis/appears
export const scrollBars = () => {
  return {
    vertical: document.body.scrollHeight > document.body.clientHeight,
    horizontal: document.body.scrollWidth > document.body.clientWidth
  }
};
const scrollbarStatus = {
  vScrollbar: true,
  no_vScrollbar: false,
  hScrollbar: true,
  no_hScrollbar: false
}
export const getScrollbarStatus = (a) => scrollbarStatus[a];
export const setScrollbarStatus = (a, s) => scrollbarStatus[a] = s;

export const dragAndDropInteraction = new DragAndDrop({
  formatConstructors: [ GeoJSON, GML, GPX, KML, SHP, TopoJSON ]
});
dragAndDropInteraction.on('addfeatures', event => {
  loadGpsFeatures(event.features, event.file.name);
});

